import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";

import { GuidedSteps } from "../components/GuidedSteps";
import { Icon } from "../components/Icon";
import { BookmarkButton } from "../components/KbCards";
import { renderKbMarkdown } from "../components/kbMarkdown";
import { kbCategoryMeta } from "../config/kbCategories";
import { api, type KbArticleDetail, type KbArticleListItem } from "../services/api";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AU", {
    dateStyle: "medium",
    timeZone: "Australia/Perth",
  });
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function useRenderedMarkdown(body: string | undefined): string {
  return useMemo(() => renderKbMarkdown(body), [body]);
}

interface TocEntry {
  id: string;
  label: string;
}

export function KBArticle() {
  const { id } = useParams<{ id: string }>();

  const { data, error, isLoading, mutate } = useSWR<KbArticleDetail>(
    id ? `/api/kb/${id}` : null,
    () => api.kb.get(id as string),
  );
  const { data: related } = useSWR<KbArticleListItem[]>(
    id ? `/api/kb/${id}/related` : null,
    () => api.kb.related(id as string),
  );

  const renderedBody = useRenderedMarkdown(data?.body);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [toc, setToc] = useState<TocEntry[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>("");

  const [voting, setVoting] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [lastVote, setLastVote] = useState<boolean | null>(null);

  // Record a view (recently-viewed) once per opened article. Fire-and-forget.
  useEffect(() => {
    if (id) void api.kb.recordView(id).catch(() => {});
  }, [id]);

  // Build the "on this page" TOC from the rendered headings; assign anchor ids.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) {
      setToc([]);
      return;
    }
    const headings = Array.from(root.querySelectorAll<HTMLElement>("h2, h3"));
    const entries: TocEntry[] = headings.map((h, i) => {
      const slug = slugify(h.textContent ?? `section-${i}`) || `section-${i}`;
      h.id = slug;
      h.style.scrollMarginTop = "84px";
      return { id: slug, label: h.textContent ?? "" };
    });
    setToc(entries);
  }, [renderedBody]);

  // Scroll-spy: the active heading is the last one whose top is above the fold.
  useEffect(() => {
    if (toc.length === 0) return;
    function onScroll() {
      let current = toc[0]?.id ?? "";
      for (const t of toc) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top <= 96) current = t.id;
      }
      setActiveHeading(current);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [toc]);

  async function handleVote(is_helpful: boolean) {
    if (!data || voting) return;
    setVoting(true);
    try {
      const result = await api.kb.vote(data.id, is_helpful);
      setLastVote(is_helpful);
      await mutate(
        { ...data, thumbs_up: result.thumbs_up, thumbs_down: result.thumbs_down, my_vote: result.is_helpful },
        { revalidate: false },
      );
      setShowComment(true);
    } finally {
      setVoting(false);
    }
  }

  async function handleCommentSubmit() {
    if (!data || commentSubmitting || !comment.trim()) return;
    const voteDirection = lastVote ?? data.my_vote ?? true;
    setCommentSubmitting(true);
    try {
      await api.kb.vote(data.id, voteDirection, comment.trim());
      setComment("");
      setShowComment(false);
    } finally {
      setCommentSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="kb-page">
        <div className="crumb">
          <Link className="back" to="/kb">
            <Icon name="book" size={15} />
            Knowledge Base
          </Link>
        </div>
        <div className="card">Loading article…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="kb-page">
        <div className="crumb">
          <Link className="back" to="/kb">
            <Icon name="book" size={15} />
            Knowledge Base
          </Link>
        </div>
        <div className="card form-error">
          {error ? `Failed to load article: ${String(error)}` : "Article not found."}
        </div>
      </div>
    );
  }

  const cat = kbCategoryMeta(data.category);
  const showToc = toc.length >= 2;

  return (
    <div className="kb-page kb-reader">
      <div className="crumb">
        <Link className="back" to="/kb">
          <Icon name="book" size={15} />
          Knowledge Base
        </Link>
        <Icon name="chevRight" size={13} />
        <Link className="back" to={`/kb/category/${encodeURIComponent(data.category)}`}>
          {data.category}
        </Link>
      </div>

      <div className={"kb-reader-layout" + (showToc ? "" : " notoc")}>
        <article>
          <header className="kb-arthead">
            <span className="kb-arthead-cat">
              <span className={`kb-cat-pin tint-${cat.tint}`}>
                <Icon name={cat.icon} size={16} />
              </span>
              <span className="mono-label">{data.category}</span>
            </span>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <h1 className="kb-art-title">{data.title}</h1>
              <div className="kb-art-headactions">
                <BookmarkButton id={data.id} initialSaved={data.is_saved} />
              </div>
            </div>
            {data.summary && <p className="kb-art-summary">{data.summary}</p>}
            <div className="kb-art-meta">
              {data.article_type === "guide" && data.steps && (
                <span className="m">
                  <Icon name="list" size={14} />
                  <strong>{data.steps.length} steps</strong>
                </span>
              )}
              <span className="m">
                <Icon name="refresh" size={14} />
                Updated {fmtDate(data.updated_at)}
              </span>
              <span className="m">
                <Icon name="shieldCheck" size={14} />
                iQuest service desk
              </span>
            </div>
          </header>
          <hr className="kb-rule" />

          <div
            className="kb-article-body"
            ref={bodyRef}
            dangerouslySetInnerHTML={{ __html: renderedBody }}
          />

          {data.article_type === "guide" && data.steps && data.steps.length > 0 && (
            <GuidedSteps steps={data.steps} articleId={data.id} />
          )}

          <div className="kb-helpfulness">
            <div className="kb-helpfulness-label">Was this article helpful?</div>
            <div className="kb-helpfulness-buttons">
              <button
                type="button"
                className={`kb-thumb-btn${data.my_vote === true ? " active-up" : ""}`}
                onClick={() => void handleVote(true)}
                disabled={voting}
                aria-pressed={data.my_vote === true}
              >
                <Icon name="thumbUp" size={16} />
                <span>{data.thumbs_up}</span>
              </button>
              <button
                type="button"
                className={`kb-thumb-btn${data.my_vote === false ? " active-down" : ""}`}
                onClick={() => void handleVote(false)}
                disabled={voting}
                aria-pressed={data.my_vote === false}
              >
                <Icon name="thumbDown" size={16} />
                <span>{data.thumbs_down}</span>
              </button>
            </div>

            {showComment && (
              <div className="kb-comment-form">
                <label htmlFor="kb-comment" className="mono-label">
                  Add a comment (optional)
                </label>
                <textarea
                  id="kb-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us more about your experience…"
                  disabled={commentSubmitting}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => void handleCommentSubmit()}
                    disabled={commentSubmitting || !comment.trim()}
                  >
                    {commentSubmitting ? "Submitting…" : "Submit comment"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowComment(false)}
                    disabled={commentSubmitting}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
          </div>

          {related && related.length > 0 && (
            <section className="kb-section" style={{ marginTop: 36 }}>
              <div className="kb-section-head">
                <div className="kb-section-title">Related articles</div>
              </div>
              <div className="kb-related">
                {related.slice(0, 4).map((r) => {
                  const rc = kbCategoryMeta(r.category);
                  return (
                    <Link key={r.id} to={`/kb/article/${r.id}`} className="kb-rel-card">
                      <span className={`kb-rel-ico tint-${rc.tint}`}>
                        <Icon name={r.article_type === "guide" ? "list" : "doc"} size={16} />
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span className="kb-rel-title">{r.title}</span>
                        <span className="kb-rel-meta">{r.category}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </article>

        {showToc && (
          <aside className="kb-toc">
            <div className="kb-toc-label">On this page</div>
            {toc.map((t) => (
              <a
                key={t.id}
                href={`#${t.id}`}
                className={"kb-toc-link" + (activeHeading === t.id ? " on" : "")}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(t.id)?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t.label}
              </a>
            ))}
          </aside>
        )}
      </div>
    </div>
  );
}

export default KBArticle;
